import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/cn';

// Tabela danych sterowana konfiguracją kolumn.
//
// Zastępuje dwa ręcznie pisane bloki <table> (kontenery i stocki), w których
// nagłówki, klasy komórek i logika pasków miały po kilkaset linii powtarzalnego
// markupu. Dokłada przy okazji sortowanie i zaznaczanie wierszy, których
// poprzednia wersja nie miała wcale — lista kontenerów wyświetlała się
// w kolejności zwróconej przez Firestore, czyli w praktyce nieprzewidywalnej.
//
// Kolumna: {
//   key, header, sortable?, align?: 'left'|'right'|'center',
//   width?: string (klasa), headerClassName?, cellClassName?,
//   render?: (row, index) => ReactNode
// }

const ALIGN = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

function SortIcon({ active, direction }) {
  if (!active) return <ChevronsUpDown size={12} className="opacity-40" />;
  return direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
}

export function DataTable({
  columns,
  rows,
  getRowId = (row) => row.id,
  sort,
  selection,
  onRowClick,
  empty,
  dense = true,
  className,
}) {
  if (rows.length === 0 && empty) return empty;

  const cellPadding = dense ? 'px-3 py-2' : 'px-4 py-3';
  const hasSelection = Boolean(selection);

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full border-collapse">
        <thead className="table-sticky-head">
          <tr className="border-b border-line">
            {hasSelection && (
              <th scope="col" className={cn('w-10 bg-elevated', cellPadding)}>
                <input
                  type="checkbox"
                  aria-label="Zaznacz wszystkie widoczne wiersze"
                  checked={selection.allSelected}
                  ref={(element) => {
                    // Stan nieokreślony: część widocznych wierszy zaznaczona.
                    if (element) element.indeterminate = selection.someSelected;
                  }}
                  onChange={selection.toggleAll}
                  className="h-3.5 w-3.5 cursor-pointer rounded border-line-strong bg-surface text-accent focus:ring-2 focus:ring-accent/30"
                />
              </th>
            )}

            {columns.map((column) => {
              const isSorted = sort?.key === column.key;
              const sortable = column.sortable && sort;

              return (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    'bg-elevated text-2xs font-semibold uppercase tracking-wide text-muted',
                    cellPadding,
                    ALIGN[column.align] || ALIGN.left,
                    column.width,
                    column.headerClassName
                  )}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => sort.onToggle(column.key)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded transition-colors hover:text-content',
                        isSorted && 'text-content',
                        column.align === 'right' && 'flex-row-reverse'
                      )}
                    >
                      {column.header}
                      <SortIcon active={isSorted} direction={sort.direction} />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const id = getRowId(row);
            const selected = hasSelection && selection.isSelected(row);

            return (
              <tr
                key={id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-line/70 transition-colors last:border-0',
                  selected ? 'bg-accent-soft' : 'hover:bg-elevated',
                  onRowClick && 'cursor-pointer'
                )}
              >
                {hasSelection && (
                  <td className={cellPadding} onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Zaznacz wiersz ${id}`}
                      checked={selected}
                      onChange={() => selection.toggle(row)}
                      className="h-3.5 w-3.5 cursor-pointer rounded border-line-strong bg-surface text-accent focus:ring-2 focus:ring-accent/30"
                    />
                  </td>
                )}

                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'text-sm text-content',
                      cellPadding,
                      ALIGN[column.align] || ALIGN.left,
                      column.cellClassName
                    )}
                  >
                    {column.render ? column.render(row, index) : row[column.key] ?? '—'}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
